/**
 * SubmissionsEmptyStateOverlay - Custom empty state for waste submissions grid.
 *
 * ARCHITECTURE: Uses hooks internally to avoid parent re-renders.
 * When omnisearch changes, only THIS component re-renders (if visible),
 * not the parent grid or page.
 *
 * Usage:
 * ```tsx
 * // Module-level constant - stable reference
 * const GRID_SLOTS = {
 *   noRowsOverlay: SubmissionsEmptyStateOverlay,
 * };
 *
 * <OmnisearchDataGridV2 slots={GRID_SLOTS} />
 * ```
 */

import { Box, Button, Typography, useTheme } from "@mui/material";
import { SubmissionStatus } from "generated-graphql/graphql";
import { useOmnisearchState, extractOmnisearchValue } from "hooks/omnisearch-v2";
import { useCurrentUser } from "hooks/useCurrentUser";
import { useTenant } from "hooks/useTenant";
import { useCallback, useMemo } from "react";
import { useNavigate } from "react-router-dom";

/**
 * Empty state overlay component for the submissions grid.
 *
 * This component uses hooks internally to subscribe to state,
 * preventing parent re-renders when omnisearch changes.
 */
export function SubmissionsEmptyStateOverlay() {
  const theme = useTheme();
  const navigate = useNavigate();
  const { tenantId } = useTenant();
  const { isStaff } = useCurrentUser();
  const { omnisearch, setOmnisearchImmediate, editOmnisearchKeyValues } = useOmnisearchState();

  // Derive filter context from omnisearch
  const statusFilterValue = extractOmnisearchValue(omnisearch, "submissionStatus");
  const readyForActionEmail = extractOmnisearchValue(omnisearch, "readyForAction");
  const readyForAction = !!readyForActionEmail;

  // Derive timeFilter equivalent from status filter
  const timeFilter = useMemo(() => {
    if (!statusFilterValue) return "";

    // Single status selections
    if (statusFilterValue === SubmissionStatus.NotStarted) return "upcoming";
    if (
      statusFilterValue === SubmissionStatus.FilingComplete ||
      statusFilterValue === SubmissionStatus.NotReporting
    ) {
      return "completed";
    }

    // Check if it's the "in-progress" group (multiple statuses)
    const inProgressStatuses = [
      SubmissionStatus.InProgress,
      SubmissionStatus.EncampInitialReview,
      SubmissionStatus.InReview,
      SubmissionStatus.ProcessingSubmission,
    ];
    const selectedStatuses = statusFilterValue.split("|");
    if (
      selectedStatuses.length > 0 &&
      selectedStatuses.every((s) => inProgressStatuses.includes(s as SubmissionStatus))
    ) {
      return "in-progress";
    }

    return "";
  }, [statusFilterValue]);

  // Handlers - stable callbacks
  const handleClearSearch = useCallback(() => {
    setOmnisearchImmediate("");
  }, [setOmnisearchImmediate]);

  const handleCreateSubmission = useCallback(() => {
    navigate(`/o/${tenantId}/waste/submissions/create`);
  }, [navigate, tenantId]);

  const handleViewUpcoming = useCallback(() => {
    editOmnisearchKeyValues([["submissionStatus", SubmissionStatus.NotStarted]]);
  }, [editOmnisearchKeyValues]);

  // Container styles for MUI DataGrid overlay
  // Fill the overlay wrapper completely with flexbox centering
  const containerStyles = {
    width: "100%",
    minHeight: 300,
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    bgcolor: theme.palette.grey50.main,
  };

  // Common text styles
  const textStyles = {
    fontFamily: "Roboto",
    fontSize: "16px",
    fontWeight: 400,
    lineHeight: 1.5,
    letterSpacing: "0.15px",
    color: theme.palette.text.secondary,
  };

  // Helper to render the centered content box
  const CenteredContent = ({ children }: { children: React.ReactNode }) => (
    <Box sx={containerStyles}>
      <Box sx={{ textAlign: "center" }}>{children}</Box>
    </Box>
  );

  // Helper to render clear search button
  const ClearSearchButton = ({ mt = 0 }: { mt?: number }) =>
    omnisearch ? (
      <Button
        variant="outlined"
        size="small"
        onClick={handleClearSearch}
        sx={{
          mt,
          display: mt ? "block" : undefined,
          mx: mt ? "auto" : undefined,
        }}
      >
        Clear Search
      </Button>
    ) : null;

  // Handle "Ready for Action" filter
  if (readyForAction) {
    return (
      <CenteredContent>
        <Typography sx={{ ...textStyles, mb: omnisearch ? 2 : 0 }}>
          {omnisearch
            ? `No submissions ready for your next action for search query: "${omnisearch}"`
            : "No submissions ready for your next action"}
        </Typography>
        <ClearSearchButton />
      </CenteredContent>
    );
  }

  // Handle "Upcoming" (Not Started) filter
  if (timeFilter === "upcoming") {
    return (
      <CenteredContent>
        <Typography sx={{ ...textStyles, mb: 2 }}>
          {omnisearch
            ? `No upcoming submissions to show for search query: "${omnisearch}"`
            : "No upcoming submissions to show"}
        </Typography>
        {isStaff && (
          <Typography
            component="span"
            onClick={handleCreateSubmission}
            sx={{
              fontSize: "14px",
              fontWeight: 500,
              lineHeight: "24px",
              letterSpacing: "0.4px",
              color: theme.palette.staff.main,
              textTransform: "uppercase",
              cursor: "pointer",
              textDecoration: "none",
              "&:hover": {
                textDecoration: "underline",
              },
            }}
          >
            Create Submission
          </Typography>
        )}
        <ClearSearchButton mt={2} />
      </CenteredContent>
    );
  }

  // Handle "In Progress" filter
  if (timeFilter === "in-progress") {
    return (
      <CenteredContent>
        <Typography sx={{ ...textStyles, mb: 2 }}>
          {omnisearch
            ? `Nothing currently in-progress for search query: "${omnisearch}"`
            : "Nothing currently in-progress"}
        </Typography>
        {isStaff && (
          <Typography sx={textStyles}>
            View{" "}
            <Typography
              component="span"
              onClick={handleViewUpcoming}
              sx={{
                ...textStyles,
                textDecoration: "underline",
                cursor: "pointer",
                "&:hover": {
                  color: theme.palette.text.primary,
                },
              }}
            >
              Upcoming Submissions
            </Typography>{" "}
            to send a submission to reviewers
          </Typography>
        )}
        <ClearSearchButton mt={2} />
      </CenteredContent>
    );
  }

  // Handle "Completed" filter
  if (timeFilter === "completed") {
    return (
      <CenteredContent>
        <Typography
          data-testid="submissions-empty-state-completed"
          sx={{ ...textStyles, mb: omnisearch ? 2 : 0 }}
        >
          {omnisearch
            ? `No completed submissions to show for search query: "${omnisearch}"`
            : "No completed submissions to show"}
        </Typography>
        <ClearSearchButton />
      </CenteredContent>
    );
  }

  // Default fallback - "All Submissions" or no specific filter
  return (
    <CenteredContent>
      <Typography sx={{ ...textStyles, mb: omnisearch ? 2 : 0 }}>
        {omnisearch
          ? `No submissions to show for search query: "${omnisearch}"`
          : "No submissions to show"}
      </Typography>
      <ClearSearchButton />
    </CenteredContent>
  );
}
